import { useState } from "react";
import Input from "../../../../components/Input/Input";
import MultipleSelect from "../../../../components/MultipleSelect/MultipleSelect";
import {
  MainSpecs,
  SessionStatuses,
  TypeSessionFilters,
} from "../../../../types/TypeSession";
import s from "./SessionFilters.module.css";

type SessionFiltersProps = {
  onApply: (
    filters: Omit<TypeSessionFilters, "page" | "limit" | "asExpert">,
  ) => void;
};

const defaultFilters = {
  participantName: "",
  sessionId: "",
  type: "" as TypeSessionFilters["type"] | "",
  status: "" as SessionStatuses | "",
  direction: "" as MainSpecs | "",
  dateFrom: "",
  dateTo: "",
};

const sessionTypeOptions = [
  { value: "", lable: "All types" },
  { value: "flexpass", lable: "FlexPass" },
  { value: "catalog", lable: "Catalog" },
  { value: "selection", lable: "Selection" },
  { value: "project", lable: "Project" },
];

const sessionStatusOptions = [
  { value: "", lable: "All statuses" },
  { value: SessionStatuses.COMPLETED, lable: "Completed" },
  { value: SessionStatuses.CANCELED, lable: "Canceled" },
  { value: SessionStatuses.REFUNDED, lable: "Refunded" },
  { value: SessionStatuses.WAIT_FOR_REFUND, lable: "Awaiting refund" },
  { value: SessionStatuses.PENDING, lable: "Pending" },
];

const directionOptions = [
  { value: "", lable: "All directions" },
  { value: MainSpecs.COACH, lable: "Coach" },
  { value: MainSpecs.PSYCHOTHERAPIST, lable: "Psychotherapist" },
];

const getEndOfSelectedDate = (date: string) => {
  const dateTo = new Date(date);
  dateTo.setUTCHours(23, 59, 0, 0);
  return dateTo;
};

const SessionFilters = ({ onApply }: SessionFiltersProps) => {
  const [filters, setFilters] = useState(defaultFilters);

  const handleChange = (field: keyof typeof defaultFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    const result: Omit<TypeSessionFilters, "page" | "limit" | "asExpert"> = {};

    if (filters.participantName.trim()) {
      result.participantName = filters.participantName.trim();
    }
    if (filters.sessionId.trim()) {
      result.sessionId = filters.sessionId.trim();
    }
    if (filters.type) {
      result.type = filters.type as TypeSessionFilters["type"];
    }
    if (filters.status) {
      result.status = filters.status as SessionStatuses;
    }
    if (filters.direction) {
      result.direction = filters.direction as MainSpecs;
    }
    if (filters.dateFrom || filters.dateTo) {
      result.date = {
        from: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
        to: filters.dateTo ? getEndOfSelectedDate(filters.dateTo) : undefined,
      };
    }

    onApply(result);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    onApply({});
  };

  return (
    <div className={s.filtersContainer}>
      <div className={`${s.filterGroup} ${s.filterGroupWide}`}>
        <Input
          inputValue={filters.participantName}
          onChangeInput={(value) => handleChange("participantName", value)}
          lable="Participant name"
          placeholder="Search by name..."
          required={false}
          isSearchinput
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 44px 10px 20px"
        />
      </div>

      <div className={`${s.filterGroup} ${s.filterGroupWide}`}>
        <Input
          inputValue={filters.sessionId}
          onChangeInput={(value) => handleChange("sessionId", value)}
          lable="Session ID"
          placeholder="Search by ID..."
          required={false}
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 20px"
        />
      </div>

      <div className={s.filterGroup}>
        <MultipleSelect
          data={sessionTypeOptions}
          setValue={(value) => handleChange("type", value as string)}
          value={filters.type ?? ""}
          multiplie={false}
          lable="Type"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>

      <div className={s.filterGroup}>
        <MultipleSelect
          data={sessionStatusOptions}
          setValue={(value) => handleChange("status", value as string)}
          value={filters.status ?? ""}
          multiplie={false}
          lable="Status"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>

      <div className={s.filterGroup}>
        <MultipleSelect
          data={directionOptions}
          setValue={(value) => handleChange("direction", value as string)}
          value={filters.direction ?? ""}
          multiplie={false}
          lable="Direction"
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 14px"
          disableSortData
        />
      </div>

      <div className={s.filterGroup}>
        <Input
          inputValue={filters.dateFrom}
          onChangeInput={(value) => handleChange("dateFrom", value)}
          lable="Date from"
          type="date"
          required={false}
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 20px"
        />
      </div>

      <div className={s.filterGroup}>
        <Input
          inputValue={filters.dateTo}
          onChangeInput={(value) => handleChange("dateTo", value)}
          lable="Date to"
          type="date"
          required={false}
          bgColor="rgba(255, 255, 255, 0.06)"
          border="1px solid rgba(255, 255, 255, 0.12)"
          padding="10px 20px"
        />
      </div>

      <div className={s.filterActions}>
        <button className={s.applyBtn} onClick={handleApply}>
          Apply
        </button>
        <button className={s.resetBtn} onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
};

export default SessionFilters;
